import type { FC } from "react";
import { memo, useEffect } from "react";

import Button from "@/design-system/button";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { AppFeatureResources, AppFeatureResourcesEn } from "@/src/homepage/AppFeature";
import { browserStatics } from "@/src/shared/BrowserStatic";
import ImageLoader from "@Components/ImageLoader";
import { HomeEventType, TelemetryEntity, UserLanguageType } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import type { QuerySmMaxToSmMin, QueryXsMax } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

import type { IAppFeatureItem } from "./AppFeatureItem";

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const smMinSmMax: QuerySmMaxToSmMin = "@media (max-width: 989px) and (min-width: 760px)";

const styles = stylex.create({
    appFeatureGiftCardComponent: {
        gridTemplateAreas: { default: "'img text' 'img btn'", [xsMax]: "'text' 'img' 'btn'" },
        alignItems: "center",
        columnGap: "10px",
        display: "grid",
        gridTemplateColumns: { default: "1fr 1fr", [xsMax]: "1fr" },
        gridTemplateRows: { default: "auto auto", [xsMax]: "repeat(3, max-content)" },
        justifyContent: "center",
        rowGap: "40px",
        textAlign: "center",
        marginBottom: { default: "100px", [xsMax]: "50px" },
        marginTop: { default: "40px", [smMinSmMax]: "100px", [xsMax]: "20px" },
    },

    imageLoaderComponent: {
        gridArea: "img",
        gridRow: { default: "1/3", [xsMax]: "2/3" },
        height: { default: "460px", [smMinSmMax]: "360px", [xsMax]: "320px" },
    },

    content: {
        gridArea: "text",
        flexDirection: { default: null, [xsMax]: "column" },
        height: "auto",
        width: { default: null, [xsMax]: "100%" },
    },

    title: {
        padding: { default: "20px 0", [xsMax]: "0" },
    },

    description: {
        fontSize: "1.25rem",
        paddingTop: { default: "20px", [xsMax]: "0" },
    },

    button: {
        gridArea: "btn",
        marginBottom: 5,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
    },

    leftOrientation: {
        flexDirection: "row-reverse",
    },
});

export interface IAppFeatureGiftCard extends IAppFeatureItem {
    isLeftOrientated: boolean;
}

export const appFeaturesGGCId = "eKartyGoodie";

export const AppFeatureGiftCard: FC<IAppFeatureGiftCard> = memo((props) => {
    const { language } = browserStatics;
    const { isMobile } = useViewportInfoContext();
    const resources = languageSelector(AppFeatureResources, AppFeatureResourcesEn);

    useEffect(
        () =>
            sendTelemetryEvent({
                eventName: `${TelemetryEntity.Homepage}-${HomeEventType.GiftCardDisplay}`,
                actionType: "Display",
                itemId: `${props.id}`,
            }),
        [],
    );

    const isEnglish = language === UserLanguageType.English;

    return (
        <div
            id={appFeaturesGGCId}
            {...stylex.props(
                styles.appFeatureGiftCardComponent,
                props.isLeftOrientated && styles.leftOrientation,
            )}
        >
            <div {...stylex.props(styles.content)}>
                {props.title && (
                    <Text
                        textElement="h2"
                        fontStyle="h2"
                        fontWeight="bold"
                        styleX={styles.title}
                        dangerouslySetInnerHTML={{ __html: props.title }}
                    />
                )}
                {props.description && (
                    <Text
                        textElement="p"
                        fontStyle="h4"
                        styleX={styles.description}
                        dangerouslySetInnerHTML={{ __html: props.description }}
                    />
                )}
            </div>
            <ImageLoader {...props.itemImage} styleX={styles.imageLoaderComponent} lazyLoading />
            <Button
                as="a"
                variant="magentaRound"
                size={isMobile ? "M" : "L"}
                href={`https://lp.goodie.pl/${isEnglish ? "ecard" : "ekarta"}`}
                target="_blank"
                styleX={styles.button}
                aria-labelledby={appFeaturesGGCId}
            >
                {resources.GGCForBusinessButton}
            </Button>
        </div>
    );
});
